import React from 'react';
import { Calendar, MapPin, Users, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';

const EventCard = ({ event }) => {
  const eventDate = new Date(event.date);
  const spotsLeft = event.max_participants - event.current_participants;
  const isFull = spotsLeft <= 0;

  const getTypeColor = (type) => {
    const colors = {
      hiking: 'bg-green-500/20 text-green-400 border-green-500/30',
      camping: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      gear_show: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      workshop: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
    };
    return colors[type] || 'bg-muted text-muted-foreground';
  };

  const getTypeLabel = (type) => {
    const labels = {
      hiking: 'Hiking',
      camping: 'Camping',
      gear_show: 'Gear Show',
      workshop: 'Workshop'
    };
    return labels[type] || type;
  };

  return (
    <div
      className="group relative overflow-hidden rounded-2xl bg-card border border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-xl"
      data-testid={`event-card-${event.event_id}`}
    >
      {/* Image */}
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={event.image_url || 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=600'}
          alt={event.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        {/* Date badge */}
        <div className="absolute top-3 left-3 bg-white dark:bg-card rounded-xl p-2 text-center shadow-lg">
          <div className="text-xs font-medium text-muted-foreground uppercase">
            {format(eventDate, 'MMM')}
          </div>
          <div className="text-2xl font-bold text-foreground leading-none">
            {format(eventDate, 'd')}
          </div>
        </div>
        
        {/* Type badge */}
        <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium border ${getTypeColor(event.event_type)}`}>
          {getTypeLabel(event.event_type)}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4 space-y-3">
        <h3 className="font-semibold text-lg line-clamp-1">{event.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2">{event.description}</p>
        
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4 text-muted-foreground">
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {event.location}
            </span>
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              {spotsLeft > 0 ? `${spotsLeft} spots left` : 'Full'}
            </span>
          </div>
        </div>
        
        {/* Tags */}
        {event.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {event.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="tag">
                {tag}
              </span>
            ))}
          </div>
        )}
        
        {/* Action */}
        <Button 
          className="w-full group/btn" 
          variant={isFull ? 'outline' : 'default'}
          disabled={isFull}
          data-testid={`event-join-btn-${event.event_id}`}
        >
          {isFull ? 'Fully Booked' : 'Learn More'}
          {!isFull && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-1" />}
        </Button>
      </div>
    </div>
  );
};

export default EventCard;
